import React, { useEffect, useRef, useState } from "react";
import { Bot, Send, X, Loader2, MessageCircle, PhoneCall, Headphones } from "lucide-react";
import { api, BASE, errMsg } from "@/lib/api";

type Msg = { role: "user" | "assistant"; text: string };
type LiveMsg = { from: string; text: string; at: string };
const AGENT = { display: "+1 (445) 305-3228", digits: "14453053228" };
const waUrl = (reason: string) => `https://wa.me/${AGENT.digits}?text=${encodeURIComponent(`Hello Trust Logistics, I need a live agent. Reason: ${reason}`)}`;

const sid = () => {
  let s = localStorage.getItem("tl_chat_sid");
  if (!s) { s = `chat_${crypto.randomUUID()}`; localStorage.setItem("tl_chat_sid", s); }
  return s;
};

const stream = async (message: string, onDelta: (t: string) => void) => {
  const r = await fetch(`${BASE}/api/assistant/chat`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sid(), message }) });
  if (!r.ok || !r.body) throw new Error(r.status === 429 ? "You're sending messages quickly. Please wait a moment." : "The assistant is unavailable right now.");
  const reader = r.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() || "";
    for (const p of parts) {
      const d = p.replace(/^data: /, "");
      if (d === "[DONE]") return;
      const j = JSON.parse(d);
      if (j.error) throw new Error(j.error);
      onDelta(j.t);
    }
  }
};

const parse = (t: string): [string, string | undefined] => {
  const m = t.match(/\[\[HANDOFF:\s*([^\]]*)\]\]/);
  return m ? [t.replace(m[0], "").trim(), m[1].trim()] : [t.replace(/\[\[[^\]]*$/, ""), undefined];
};

const Handoff = ({ reason, onLive }: { reason: string; onLive: (r: string) => void }) => (
  <div className="mt-2 space-y-2 rounded-2xl border border-brass/50 bg-brass-soft p-3" data-testid="assistant-handoff">
    <p className="text-xs font-semibold text-navy">Connect with a live agent</p>
    <p className="text-[11px] text-navy/60">Reason: {reason}</p>
    <div className="flex flex-wrap gap-2">
      <button onClick={() => onLive(reason)} className="inline-flex items-center gap-1.5 rounded-full bg-brass px-3 py-1.5 text-xs font-semibold text-navy" data-testid="assistant-handoff-livechat"><Headphones size={13} /> Live chat here</button>
      <a href={waUrl(reason)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-3 py-1.5 text-xs font-semibold text-white" data-testid="assistant-handoff-whatsapp"><MessageCircle size={13} /> WhatsApp agent</a>
      <a href={`tel:+${AGENT.digits}`} className="inline-flex items-center gap-1.5 rounded-full bg-navy px-3 py-1.5 text-xs font-semibold text-white" data-testid="assistant-handoff-call"><PhoneCall size={13} /> Call {AGENT.display}</a>
    </div>
  </div>
);

const LiveStart = ({ onStart, onCancel }: { onStart: (r: string) => void; onCancel: () => void }) => {
  const [reason, setReason] = useState("");
  const ok = reason.trim().length >= 5;
  return (
    <div className="space-y-3 p-4" data-testid="live-start">
      <p className="text-sm font-semibold">Talk to a live agent</p>
      <p className="text-xs text-navy/60">Live agents handle urgent and booking matters, such as cancellations, changes, payments or an airport pickup today. Tell us briefly why you need an agent.</p>
      <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="e.g. I need to cancel booking TL-S-… for tomorrow" className="field text-sm" data-testid="live-start-reason" />
      <div className="flex flex-wrap gap-2">
        <button disabled={!ok} onClick={() => onStart(reason.trim())} className="btn-gold px-4 py-2 text-xs disabled:opacity-50" data-testid="live-start-chat"><Headphones size={13} /> Start live chat</button>
        <a href={ok ? waUrl(reason.trim()) : undefined} target="_blank" rel="noreferrer" aria-disabled={!ok} className={`inline-flex items-center gap-1.5 rounded-full bg-[#25D366] px-4 py-2 text-xs font-semibold text-white ${ok ? "" : "pointer-events-none opacity-50"}`} data-testid="live-start-whatsapp"><MessageCircle size={13} /> WhatsApp</a>
        <button onClick={onCancel} className="text-xs underline" data-testid="live-start-cancel">Back to assistant</button>
      </div>
    </div>
  );
};

const LiveChat = ({ onBack }: { onBack: () => void }) => {
  const [msgs, setMsgs] = useState<LiveMsg[]>([]);
  const [online, setOnline] = useState(false);
  const [status, setStatus] = useState("open");
  const [text, setText] = useState("");
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let alive = true;
    const poll = () => api.get(`/livechat/${sid()}`).then((r) => { if (!alive) return; setMsgs(r.data.messages || []); setOnline(r.data.agent_online); setStatus(r.data.status); }).catch(() => {});
    poll();
    const t = setInterval(poll, 4000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);
  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const m = text.trim();
    if (!m) return;
    setText("");
    try { await api.post("/livechat/message", { session_id: sid(), text: m }); setMsgs((p) => [...p, { from: "visitor", text: m, at: new Date().toISOString() }]); } catch (err) { setMsgs((p) => [...p, { from: "system", text: errMsg(err), at: "" }]); }
  };
  return (
    <>
      <div className={`px-4 py-2 text-[11px] ${online ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid="live-agent-status">
        {online ? "An agent is online and will reply here shortly." : "Our agents are offline right now. Leave your message here and we'll reply in this chat, or WhatsApp " + AGENT.display + "."}
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="live-messages">
        {msgs.map((m, i) => (
          <div key={i} className={`max-w-[85%] whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm ${m.from === "visitor" ? "ml-auto bg-navy text-white" : m.from === "agent" ? "bg-brass-soft text-navy" : "mx-auto bg-transparent text-center text-xs text-navy/50"}`} data-testid={`live-msg-${m.from}`}>
            {m.from === "agent" && <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-brass-deep">Trust Logistics agent</p>}{m.text}
          </div>
        ))}
        <div ref={end} />
      </div>
      {status === "open" ? (
        <form onSubmit={send} className="flex gap-2 border-t border-navy/10 p-3">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Message our team…" className="field flex-1" data-testid="live-input" />
          <button className="btn-gold px-4" aria-label="Send" data-testid="live-send"><Send size={15} /></button>
        </form>
      ) : <div className="border-t border-navy/10 p-3 text-center text-xs"><button onClick={onBack} className="underline" data-testid="live-back">This chat has ended — back to assistant</button></div>}
    </>
  );
};

export const AssistantWidget = () => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"ai" | "start" | "live">(() => (localStorage.getItem("tl_live") === "1" ? "live" : "ai"));
  const [msgs, setMsgs] = useState<Msg[]>([{ role: "assistant", text: "Hi! I'm the Trust Logistics virtual assistant (AI). Ask me about stays, vehicles, airport pickups or Gant Estates. Need a live agent for something urgent, like a booking or cancellation? Tell me briefly why and I'll connect you." }]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => {
    end.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);
  const goLive = async (reason: string) => {
    try { await api.post("/livechat/start", { session_id: sid(), reason }); localStorage.setItem("tl_live", "1"); setMode("live"); } catch (err) { setMsgs((p) => [...p, { role: "assistant", text: errMsg(err) }]); setMode("ai"); }
  };
  const backToAi = () => { localStorage.removeItem("tl_live"); setMode("ai"); };
  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const m = text.trim();
    if (!m || busy) return;
    setText("");
    setBusy(true);
    setMsgs((p) => [...p, { role: "user", text: m }, { role: "assistant", text: "" }]);
    const add = (t: string) => setMsgs((p) => { const c = [...p]; c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + t }; return c; });
    try { await stream(m, add); } catch (err: any) { add(err.message); } finally { setBusy(false); }
  };
  return (
    <>
      {open && (
        <div className="fixed bottom-24 left-4 right-4 z-50 flex max-h-[72vh] flex-col overflow-hidden rounded-3xl border border-navy/10 bg-white shadow-2xl sm:left-6 sm:right-auto sm:w-[380px]" data-testid="assistant-panel">
          <div className="flex items-center justify-between gap-2 bg-navy px-5 py-4 text-white">
            <div><p className="font-serif font-bold">{mode === "live" ? "Live chat" : "Trust Assistant"}</p><p className="font-mono text-[9px] uppercase tracking-[0.2em] text-brass">{mode === "live" ? "Real Trust Logistics team" : "AI · not a live agent"}</p></div>
            <div className="flex items-center gap-3">
              {mode === "ai" && <button onClick={() => setMode("start")} className="rounded-full border border-brass/60 px-3 py-1 text-[11px] font-semibold text-brass" data-testid="assistant-live-agent-button">Live agent</button>}
              {mode === "live" && <button onClick={backToAi} className="text-[11px] underline" data-testid="assistant-back-ai">AI assistant</button>}
              <button onClick={() => setOpen(false)} aria-label="Close" data-testid="assistant-close"><X size={18} /></button>
            </div>
          </div>
          {mode === "start" ? <LiveStart onStart={goLive} onCancel={() => setMode("ai")} /> : mode === "live" ? <LiveChat onBack={backToAi} /> : (
            <>
              <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="assistant-messages">
                {msgs.map((m, i) => {
                  const [t, handoff] = m.role === "assistant" ? parse(m.text) : [m.text, undefined];
                  return (
                    <div key={i} className={`max-w-[85%] whitespace-pre-line rounded-2xl px-3.5 py-2.5 text-sm ${m.role === "user" ? "ml-auto bg-navy text-white" : "bg-stone-canvas text-navy"}`} data-testid={`assistant-msg-${m.role}`}>
                      {t || (!handoff && <Loader2 size={14} className="animate-spin" />)}
                      {handoff && <Handoff reason={handoff} onLive={goLive} />}
                    </div>
                  );
                })}
                <div ref={end} />
              </div>
              <form onSubmit={send} className="flex gap-2 border-t border-navy/10 p-3">
                <input value={text} onChange={(e) => setText(e.target.value)} maxLength={1500} placeholder="Ask a question…" className="field flex-1" data-testid="assistant-input" />
                <button disabled={busy} className="btn-gold px-4" aria-label="Send" data-testid="assistant-send"><Send size={15} /></button>
              </form>
            </>
          )}
        </div>
      )}
      <button onClick={() => setOpen((o) => !o)} className="fixed bottom-6 left-6 z-40 inline-flex h-14 items-center gap-2 rounded-full bg-navy px-5 text-sm font-semibold text-white shadow-lg ring-2 ring-brass/70 transition-transform hover:scale-105" data-testid="assistant-open-button">
        <Bot size={20} className="text-brass" /> <span className="hidden sm:inline">Assistant & live chat</span>
      </button>
    </>
  );
};
